/**
 * WeaveIn.ai Platform - Health Plans Management Page
 * Main listing page for health plan organizations with CRUD operations
 */

import { useTabbedChrome } from '@/components/weavein';
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Building2,
  Plus,
  Search,
  Filter,
  MoreVertical,
  Edit,
  Trash2,
  Eye,
  Phone,
  Globe,
  MapPin,
  RefreshCw,
  X,
} from 'lucide-react';
import { useAppTranslation } from '@/contexts/TranslationContext';
import PhoneInput from '@/components/ui/PhoneInput';
import {
  healthPlansAPI,
  HealthPlan,
  HealthPlanCreate,
  PLAN_TYPES,
  PlanType,
} from '@/services/api/healthPlans';

// ============================================================================
// Health Plan Form Modal
// ============================================================================

interface HealthPlanFormProps {
  plan?: HealthPlan | null;
  onClose: () => void;
  onSave: () => void;
}

const HealthPlanForm: React.FC<HealthPlanFormProps> = ({ plan, onClose, onSave }) => {
  const { t } = useAppTranslation();
  const [formData, setFormData] = useState<HealthPlanCreate>({
    name: plan?.name || '',
    short_name: plan?.short_name || '',
    legal_name: plan?.legal_name || '',
    plan_type: plan?.plan_type || 'HEALTH_PLAN',
    naic_code: plan?.naic_code || '',
    tax_id: plan?.tax_id || '',
    electronic_payer_id: plan?.electronic_payer_id || '',
    headquarters_address_line1: plan?.headquarters_address_line1 || '',
    headquarters_city: plan?.headquarters_city || '',
    headquarters_state: plan?.headquarters_state || '',
    headquarters_zip: plan?.headquarters_zip || '',
    main_phone: plan?.main_phone || '',
    member_services_phone: plan?.member_services_phone || '',
    provider_services_phone: plan?.provider_services_phone || '',
    website: plan?.website || '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      setError(t('healthPlans.errors.planNameRequired', 'Plan name is required'));
      return;
    }

    setSaving(true);
    setError(null);

    try {
      if (plan) {
        await healthPlansAPI.updateHealthPlan(plan.id, formData);
      } else {
        await healthPlansAPI.createHealthPlan(formData);
      }
      onSave();
    } catch (err: any) {
      setError(err.message || 'Failed to save health plan');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div
        className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
            {plan ? t('healthPlans.form.editHealthPlan', 'Edit Health Plan') : t('healthPlans.form.addHealthPlan', 'Add Health Plan')}
          </h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700 dark:text-gray-400">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4 overflow-y-auto max-h-[calc(90vh-140px)]">
          {error && (
            <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-300 text-sm">
              {error}
            </div>
          )}

          {/* Row 1: Name & Short Name */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('healthPlans.form.planName', 'Plan Name')} *
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                placeholder={t('healthPlans.form.planNameExample', 'e.g., Blue Cross Blue Shield')}
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('healthPlans.form.shortName', 'Short Name')}
              </label>
              <input
                type="text"
                value={formData.short_name || ''}
                onChange={(e) => setFormData({ ...formData, short_name: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                placeholder={t('healthPlans.form.shortNameExample', 'e.g., BCBS')}
              />
            </div>
          </div>

          {/* Row 2: Plan Type & Legal Name */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('healthPlans.form.planType', 'Plan Type')}
              </label>
              <select
                value={formData.plan_type}
                onChange={(e) => setFormData({ ...formData, plan_type: e.target.value as PlanType })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
              >
                {PLAN_TYPES.map((pt) => (
                  <option key={pt.value} value={pt.value}>{pt.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('healthPlans.form.legalName', 'Legal Name')}
              </label>
              <input
                type="text"
                value={formData.legal_name || ''}
                onChange={(e) => setFormData({ ...formData, legal_name: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          {/* Row 3: Identifiers */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('healthPlans.form.naicCode', 'NAIC Code')}
              </label>
              <input
                type="text"
                value={formData.naic_code || ''}
                onChange={(e) => setFormData({ ...formData, naic_code: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('healthPlans.form.taxId', 'Tax ID (EIN)')}
              </label>
              <input
                type="text"
                value={formData.tax_id || ''}
                onChange={(e) => setFormData({ ...formData, tax_id: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('healthPlans.form.electronicPayerId', 'Electronic Payer ID')}
              </label>
              <input
                type="text"
                value={formData.electronic_payer_id || ''}
                onChange={(e) => setFormData({ ...formData, electronic_payer_id: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          {/* Row 4: Address */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t('healthPlans.form.headquartersAddress', 'Headquarters Address')}
            </label>
            <input
              type="text"
              value={formData.headquarters_address_line1 || ''}
              onChange={(e) => setFormData({ ...formData, headquarters_address_line1: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
              placeholder={t('healthPlans.form.streetAddress', 'Street Address')}
            />
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="col-span-2">
              <input
                type="text"
                value={formData.headquarters_city || ''}
                onChange={(e) => setFormData({ ...formData, headquarters_city: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                placeholder={t('healthPlans.form.city', 'City')}
              />
            </div>
            <div>
              <input
                type="text"
                value={formData.headquarters_state || ''}
                onChange={(e) => setFormData({ ...formData, headquarters_state: e.target.value.toUpperCase().slice(0, 2) })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                placeholder={t('healthPlans.form.state', 'State')}
                maxLength={2}
              />
            </div>
            <div>
              <input
                type="text"
                value={formData.headquarters_zip || ''}
                onChange={(e) => setFormData({ ...formData, headquarters_zip: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                placeholder={t('healthPlans.form.zip', 'ZIP')}
              />
            </div>
          </div>

          {/* Row 5: Phone Numbers */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Main Phone
              </label>
              <PhoneInput
                value={formData.main_phone || ''}
                onChange={(v) => setFormData({ ...formData, main_phone: v })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Member Services
              </label>
              <PhoneInput
                value={formData.member_services_phone || ''}
                onChange={(v) => setFormData({ ...formData, member_services_phone: v })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Provider Services
              </label>
              <PhoneInput
                value={formData.provider_services_phone || ''}
                onChange={(v) => setFormData({ ...formData, provider_services_phone: v })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          {/* Row 6: Website */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Website
            </label>
            <input
              type="url"
              value={formData.website || ''}
              onChange={(e) => setFormData({ ...formData, website: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
              placeholder="https://"
            />
          </div>
        </form>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={saving}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
          >
            {saving ? 'Saving...' : plan ? 'Update' : 'Create'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// Main Component
// ============================================================================

interface HealthPlansPageProps {
  onSelectPlan?: (planId: number) => void;
}

export default function HealthPlansPage({ onSelectPlan }: HealthPlansPageProps) {
  const tabbed = useTabbedChrome();
  const navigate = useNavigate();
  const [plans, setPlans] = useState<HealthPlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [planTypeFilter, setPlanTypeFilter] = useState<PlanType | ''>('');
  const [showForm, setShowForm] = useState(false);
  const [editingPlan, setEditingPlan] = useState<HealthPlan | null>(null);
  const [total, setTotal] = useState(0);

  // Navigate to plan detail - use prop callback if provided, otherwise use router
  const handleViewPlan = (planId: number) => {
    if (onSelectPlan) {
      onSelectPlan(planId);
    } else {
      navigate(`/admin/health-plans/${planId}`);
    }
  };

  // Load health plans
  const loadPlans = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const params: any = { status: 'ACTIVE' };
      if (search) params.search = search;
      if (planTypeFilter) params.plan_type = planTypeFilter;

      const result = await healthPlansAPI.listHealthPlans(params);
      setPlans(result.plans);
      setTotal(result.total);
    } catch (err: any) {
      setError(err.message || 'Failed to load health plans');
    } finally {
      setLoading(false);
    }
  }, [search, planTypeFilter]);

  useEffect(() => {
    loadPlans();
  }, [loadPlans]);

  // Handle delete
  const handleDelete = async (planId: number) => {
    if (!window.confirm('Are you sure you want to deactivate this health plan?')) {
      return;
    }

    try {
      await healthPlansAPI.deleteHealthPlan(planId);
      loadPlans();
    } catch (err: any) {
      setError(err.message || 'Failed to delete health plan');
    }
  };

  // Handle form save
  const handleFormSave = () => {
    setShowForm(false);
    setEditingPlan(null);
    loadPlans();
  };

  // Get plan type label
  const getPlanTypeLabel = (type: PlanType) => {
    return PLAN_TYPES.find((pt) => pt.value === type)?.label || type;
  };

  // Get plan type color
  const getPlanTypeColor = (type: PlanType) => {
    const colors: Record<PlanType, string> = {
      HEALTH_PLAN: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
      TPA: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200',
      PBM: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
      SELF_INSURED: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200',
      GOVERNMENT: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
      OTHER: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200',
    };
    return colors[type] || colors.OTHER;
  };

  return (
    <div className="h-full flex flex-col bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-6 py-4">
        <div className="flex justify-between items-center">
          <div>
            {!tabbed && (
            <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 flex items-center gap-2">
              <Building2 className="h-6 w-6 text-blue-600" />
              Health Plans
            </h1>
            )}
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              Manage health plan organizations, TPAs, and PBMs
            </p>
          </div>
          <button
            onClick={() => setShowForm(true)}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Plus className="w-5 h-5" />
            Add Health Plan
          </button>
        </div>

        {/* Filters */}
        <div className="flex gap-4 mt-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search health plans..."
              className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div className="relative">
            <Filter className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <select
              value={planTypeFilter}
              onChange={(e) => setPlanTypeFilter(e.target.value as PlanType | '')}
              className="pl-10 pr-8 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 appearance-none"
            >
              <option value="">All Types</option>
              {PLAN_TYPES.map((pt) => (
                <option key={pt.value} value={pt.value}>{pt.label}</option>
              ))}
            </select>
          </div>
          <button
            onClick={loadPlans}
            className="p-2 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            title="Refresh"
          >
            <RefreshCw className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto p-6">
        {/* Error */}
        {error && (
          <div className="mb-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-300">
            {error}
            <button onClick={() => setError(null)} className="ml-2 underline">Dismiss</button>
          </div>
        )}

        {/* Loading */}
        {loading ? (
          <div className="flex flex-col items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            <p className="mt-4 text-gray-600 dark:text-gray-400">Loading health plans...</p>
          </div>
        ) : plans.length === 0 ? (
          /* Empty State */
          <div className="flex flex-col items-center justify-center h-64 text-center">
            <Building2 className="w-16 h-16 text-gray-400 mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
              No health plans yet
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              Add your first health plan to get started
            </p>
            <button
              onClick={() => setShowForm(true)}
              className="flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Plus className="w-5 h-5" />
              Add Health Plan
            </button>
          </div>
        ) : (
          /* Plans Grid */
          <>
            <div className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              Showing {plans.length} of {total} health plans
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {plans.map((plan) => (
                <div
                  key={plan.id}
                  className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 hover:shadow-lg transition-shadow"
                >
                  <div className="p-6">
                    <div className="flex justify-between items-start mb-3">
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                          {plan.name}
                        </h3>
                        {plan.short_name && (
                          <p className="text-sm text-gray-500 dark:text-gray-400">
                            {plan.short_name}
                          </p>
                        )}
                      </div>
                      <span className={`px-2 py-1 text-xs rounded-full ${getPlanTypeColor(plan.plan_type)}`}>
                        {getPlanTypeLabel(plan.plan_type)}
                      </span>
                    </div>

                    <div className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
                      {plan.headquarters_city && plan.headquarters_state && (
                        <div className="flex items-center gap-2">
                          <MapPin className="w-4 h-4" />
                          <span>{plan.headquarters_city}, {plan.headquarters_state}</span>
                        </div>
                      )}
                      {plan.main_phone && (
                        <div className="flex items-center gap-2">
                          <Phone className="w-4 h-4" />
                          <span>{plan.main_phone}</span>
                        </div>
                      )}
                      {plan.website && (
                        <div className="flex items-center gap-2">
                          <Globe className="w-4 h-4" />
                          <a
                            href={plan.website}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:underline truncate"
                          >
                            {plan.website.replace(/^https?:\/\//, '')}
                          </a>
                        </div>
                      )}
                      {plan.electronic_payer_id && (
                        <div className="text-xs text-gray-500">
                          Payer ID: {plan.electronic_payer_id}
                        </div>
                      )}
                    </div>

                    {/* LOBs & Networks Summary */}
                    <div className="mt-4 flex gap-4 text-xs text-gray-500 dark:text-gray-400">
                      {plan.lines_of_business && (
                        <span>{plan.lines_of_business.length} LOBs</span>
                      )}
                      {plan.networks && (
                        <span>{plan.networks.length} Networks</span>
                      )}
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="px-6 py-3 bg-gray-50 dark:bg-gray-700/50 border-t border-gray-200 dark:border-gray-700 flex justify-between items-center">
                    <button
                      onClick={() => handleViewPlan(plan.id)}
                      className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-700 dark:text-blue-400"
                    >
                      <Eye className="w-4 h-4" />
                      View Details
                    </button>
                    <div className="flex gap-2">
                      <button
                        onClick={() => {
                          setEditingPlan(plan);
                          setShowForm(true);
                        }}
                        className="p-2 text-gray-600 hover:text-gray-700 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition-colors"
                        title="Edit"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(plan.id)}
                        className="p-2 text-red-600 hover:text-red-700 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/30 rounded-lg transition-colors"
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      {/* Form Modal */}
      {showForm && (
        <HealthPlanForm
          plan={editingPlan}
          onClose={() => {
            setShowForm(false);
            setEditingPlan(null);
          }}
          onSave={handleFormSave}
        />
      )}
    </div>
  );
}
